import type { Paginated, PaginationQuery } from "@/configs/request";
import type {
  DisputeMessage,
  DisputeParty,
  MeetingActivity,
  MeetingMessage,
  Report,
  ReportStatus,
  RoomEvent,
} from "@/types";

import { BaseApi } from "./base.api";

export interface ReportFilter extends PaginationQuery {
  status?: ReportStatus;
}

class AdminReportsApi extends BaseApi {
  private readonly baseUrl = "/admin/reports";

  list(filter: ReportFilter = {}) {
    return this.request<Paginated<Report>>(this.baseUrl, { query: { ...filter } });
  }

  /**
   * Ko'rib chiqildi: asosli yoki asossiz. Ikki tasdiqlangan shikoyat → blok (PRD §12).
   *
   * `hostCoffees` — mezbonga tegadigan qahva soni (ADR 0015). Qaror
   * PROFILGA qanday ta'sir qilishini `outcome` belgilaydi, PULNI esa
   * shu son: kechikish nizosida ikkala chekka ham noto'g'ri javob.
   * Bo'sh qoldirilsa backend qarordan hisoblaydi — asosli shikoyatda
   * hammasi mehmonga, asossizda hammasi mezbonga.
   */
  resolve(id: string, outcome: "resolved" | "rejected", note?: string, hostCoffees?: number) {
    return this.request<Report>(`${this.baseUrl}/${id}/resolve`, {
      method: "POST",
      body: { outcome, note, hostCoffees },
    });
  }

  /**
   * Nizo suhbati — bitta tomonniki.
   *
   * Ikkala tomon ALOHIDA gaplashadi va bir-birining javobini
   * ko'rmaydi: umumiy suhbat nizoni janjalga aylantirardi.
   */
  messages(id: string, party: DisputeParty) {
    return this.request<DisputeMessage[]>(`${this.baseUrl}/${id}/messages`, { query: { party } });
  }

  /**
   * Tomonga savol berish.
   *
   * Shikoyat QILINGAN odam aynan shu xabardan keyin shikoyat
   * borligini biladi — shuning uchun alohida ruxsat (`reports.reply`).
   */
  reply(id: string, party: DisputeParty, body: string) {
    return this.request<DisputeMessage>(`${this.baseUrl}/${id}/messages`, {
      method: "POST",
      body: { party, body },
    });
  }
}

/**
 * Uchrashuv yozishmalari — nizoni tekshirish uchun.
 *
 * Chat aynan tomonlar bir-birining raqamini ko'rmasligi uchun
 * qo'yilgan (ADR 0011); uni moderator o'qishi — shu va'daga
 * qo'yilgan istisno. Har bir so'rov audit jurnaliga tushadi va
 * alohida ruxsat talab qiladi (`chats.read`).
 */
class AdminMeetingChatApi extends BaseApi {
  /**
   * Xabarni to'sish — O'CHIRISH EMAS.
   *
   * Matn tomonlarga ko'rinmay qoladi, bazada va shu ro'yxatda esa
   * to'liq saqlanadi: "u menga qo'pol yozdi" degan shikoyatni
   * tekshirishning yagona yo'li — o'sha yozuvning o'zi.
   */
  redact(messageId: string, reason: string) {
    return this.request<MeetingMessage>(`/admin/invitations/messages/${messageId}/redact`, {
      method: "POST",
      body: { reason },
    });
  }

  /** To'sishni bekor qilish. Sabab so'ralmaydi — bu xatoni tuzatish. */
  restore(messageId: string) {
    return this.request<MeetingMessage>(`/admin/invitations/messages/${messageId}/restore`, {
      method: "POST",
    });
  }

  messages(invitationId: string) {
    return this.request<Paginated<MeetingMessage>>(`/admin/invitations/${invitationId}/chat`, {
      query: { limit: 100 },
    });
  }
}

export const adminReportsApi = new AdminReportsApi();
/**
 * Onlayn xonaga kirish jurnali — nizoning asosiy dalili (ADR 0013).
 *
 * Suhbatning O'ZIDAN ALOHIDA va yengilroq ruxsat bilan
 * (`reports.read` vs `chats.read`): kim qachon kirgani shaxsiy
 * yozishma emas, va nizoning katta qismi aynan shu bilan hal
 * bo'ladi — yozishmani ochishga hojat qolmaydi.
 */
class AdminRoomEventsApi extends BaseApi {
  list(invitationId: string) {
    return this.request<RoomEvent[]>(`/admin/invitations/${invitationId}/room/events`);
  }

  /**
   * Ishtirok xulosasi: kim necha marta kirgan va jami qancha turgan.
   *
   * Jurnaldan ALOHIDA so'rov: jurnal webhook'ga bog'liq va bo'sh
   * bo'lishi mumkin, bu esa har doim to'ladi. "Kirdi va bir daqiqada
   * chiqdi" degan da'vo aynan shu raqamlarda ko'rinadi.
   */
  activity(invitationId: string) {
    return this.request<MeetingActivity>(`/admin/invitations/${invitationId}/room/activity`);
  }
}

export const adminMeetingChatApi = new AdminMeetingChatApi();
export const adminRoomEventsApi = new AdminRoomEventsApi();
